import { useAuthStore } from "~/store/auth/auth.store";
import { useEmbedStore } from "~/store/embed/embed.store";
import { useAuth } from "~/composables/auth/useAuth";
import { isRouteForbidden, forbiddenError, trainerSessionRedirect } from "~/utils/route-access";
import { isOidcRoute, OidcDiscoveryUnavailableError } from "~/utils/helpers/auth/oidc-contract";
import { RolesEnums } from "~/utils/enums/roles.enums";
import { HttpStatusEnum } from "~/utils/enums/common.enums";
import { RoleSyncResultEnum, AuthModeEnum } from "~/utils/enums/auth.enums";
import { AuthSessionUnavailableError, isAuthDocumentUnloading, isAuthLoggingOut } from "~/utils/helpers/auth/lifecycle";
import { authEntryPath, isEmbeddedAuth, DOCEBO_ENTRY_PATH } from "~/utils/helpers/auth/browser-context";
/**
 * process.client when the middleware have to be used on client side (from navigation, nuxt-link etc)
 * else when the middleware have to be used on server side (from URL)
 * */
export default defineNuxtRouteMiddleware(async (to, from) => {
  if (isAuthDocumentUnloading() || isAuthLoggingOut()) return false;
  /**
   * Global Middleware, it redirects if user is not logged in
   */
  const authStore = useAuthStore();
  const config = useRuntimeConfig();
  if (to.path === DOCEBO_ENTRY_PATH) {
    if (isEmbeddedAuth()) return;
    // Direct links use the regular login policy and session, regardless of embed=true.
    useEmbedStore().setEmbed(false);
    useAuth().saveReturnTo(typeof to.query.next_url === "string" ? to.query.next_url : "/home");
    return navigateTo("/", { replace: true });
  }
  if (isEmbeddedAuth() && (!authStore.isToken || isOidcRoute(to.path) || to.path === "/"))
    return navigateTo(DOCEBO_ENTRY_PATH, { replace: true });
  if (isOidcRoute(to.path)) return;
  if (authStore.mode === AuthModeEnum.IMPERSONATION && !authStore.isAccessTokenValid()) {
    return authStore.exitImpersonation();
  }

  // Restore tokens from cookies if store is empty (e.g. hard refresh in SPA mode)
  if (!authStore.isToken) {
    try {
      await authStore.restoreSession();
    } catch (error) {
      if (isAuthDocumentUnloading() || isAuthLoggingOut()) return false;
      if (error instanceof OidcDiscoveryUnavailableError) return false;
      if (error instanceof AuthSessionUnavailableError)
        return showError({ statusCode: HttpStatusEnum.INTERNAL_SERVER_ERROR });
      authStore.silentLogOut();
    }
  }
  if (isAuthDocumentUnloading() || isAuthLoggingOut()) return false;

  // A failed startup profile request leaves valid credentials but no user. Retry once before
  // checking authentication; a continuing outage must not clear the session or expose the page.
  if (authStore.isAccessTokenValid() && !authStore.user) {
    await authStore.login(to.query.embed === "true");
    if (isAuthDocumentUnloading() || isAuthLoggingOut()) return false;
    if (authStore.isAccessTokenValid() && !authStore.user) {
      return showError({ statusCode: HttpStatusEnum.INTERNAL_SERVER_ERROR });
    }
  }

  if (config.public.ENV !== "PROD") {
    console.log("Auth Middleware");
  }

  const { meta } = to;
  const { fullPath } = from;
  const redirectCookie = useCookie("redirect_url");

  /**
   * Disabled AuthMiddle with this PageMeta
   * definePageMeta({
   *   isLoggedIn: false
   * });
   */

  if (typeof meta.isLoggedIn === "boolean" && meta.isLoggedIn === false) {
    if (from.fullPath !== "/") {
      if (!authStore.isLoggedIn()) {
        if (redirectCookie.value === "none") {
          redirectCookie.value = null;
        } else if (!from.fullPath.includes("/sso")) {
          redirectCookie.value = fullPath;
        }
      } else {
        redirectCookie.value = null;
      }
    }
  } else {
    try {
      await authStore.refreshToken();
    } catch (error) {
      if (error instanceof OidcDiscoveryUnavailableError) return false;
      if (error instanceof AuthSessionUnavailableError)
        return showError({ statusCode: HttpStatusEnum.INTERNAL_SERVER_ERROR });
      throw error;
    }
    if (isAuthDocumentUnloading() || isAuthLoggingOut()) return false;
    if (!authStore.isLoggedIn()) {
      useAuth().saveReturnTo(to.fullPath);
      // SSR version (before ssr: false):
      if (import.meta.client) {
        authStore.silentLogOut();
        // Remember where they were headed so the login funnel can finish the journey —
        // `useAuth.handleExternalRedirect()` consumes this cookie. This used to write the "none"
        // sentinel, which the SECOND pass of this middleware (the one for "/") reads as "the app
        // sent you here, forget it" and nulls out just above, so a deep link opened while logged
        // out always ended on /home. "none" belongs to an explicit `logOut()` (auth.store.ts),
        // where the page you just left is not a destination you were trying to reach.
        if (!isEmbeddedAuth()) redirectCookie.value = to.fullPath;
        return navigateTo(authEntryPath());
      } else {
        return authStore.logOut();
      }
    } else if (import.meta.client) {
      // A role change hasn't been acknowledged yet: keep the "Vos droits ont été modifiés"
      // page up on every navigation until the user clicks through it (error.vue
      // clears the flag), so a competing navigation can't flicker it away.
      if (authStore.roleConflictPending) {
        return showError(forbiddenError());
      }

      // RULE: a server-side role change detected on navigation → error page.
      // Re-sync roles + JWT to the DB; if the effective roles changed, show the
      // "Vos droits ont été modifiés" page so the user acknowledges it (the whole UI
      // re-syncs on the way out) — unless they only gained roles ranked below their
      // highest one (LOWER_ROLES_GAINED), which lets the navigation through. Reload /
      // direct-URL landing (from === to) skips the re-sync — auth-plugin already
      // loaded the profile in that case.
      if (from.fullPath !== to.fullPath) {
        try {
          const result = await authStore.revalidateRoles();
          if (isAuthDocumentUnloading() || isAuthLoggingOut()) return false;

          // The session died under us (no profile, or roles and JWT could not be re-synced).
          // Take the same exit as the logged-out branch above — carrying on would render a
          // route with no `meta.roles` for a user who now holds none.
          if (result === RoleSyncResultEnum.SESSION_LOST) {
            if (!isEmbeddedAuth()) redirectCookie.value = "none";
            return authStore.handleSessionLoss();
          }

          if (result === RoleSyncResultEnum.CHANGED) {
            authStore.roleConflictPending = true;
            return showError(forbiddenError());
          }
        } catch (error) {
          // Never let a network blip reject inside the middleware: that aborts the
          // navigation itself. Keep the roles we hold, let the route guard below decide,
          // and re-check on the next navigation or visibility change.
          console.error("auth.global: revalidateRoles failed", error);
        }
      }

      // A trainer opening /sessions?id= goes to their own view. Must stay before the 403 below, which fires
      // before any /sessions page middleware could run.
      const trainerRoute = trainerSessionRedirect(
        to,
        authStore.getRoles,
        authStore.isInternalTrainer,
        useRouter().resolve("/my-sessions").meta,
      );
      if (trainerRoute) return navigateTo(trainerRoute, { replace: true });

      // Navigating to a page the (unchanged) roles don't permit → error page too.
      if (
        isRouteForbidden(
          meta.roles as RolesEnums[] | undefined,
          meta.excludeRoles as RolesEnums[] | undefined,
          authStore.getRoles,
        )
      ) {
        return showError(forbiddenError());
      }
    }
  }
  // }
});
